import {
  BoundNumberField,
  Button,
  Css,
  IconButton,
  TestIds,
  ToggleChipGroup,
  useComputed,
  useModal,
} from "@homebound/beam";
import { fragment, ObjectState } from "@homebound/form-state";
import { observer } from "mobx-react";
import { ReadyPlanOptionGroupPersonalizationType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { AddSlotModal } from "src/routes/libraries/design-catalog/components/AddSlotModal";
import { EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment } from "src/routes/libraries/design-catalog/personalization-option/EditPersonalizationOptionWizard.generated";
import {
  addExistingMaterials,
  buildRpoIdToRpogIdMap,
  FormValue,
  maybeFilterByPlaceholder,
} from "src/routes/libraries/design-catalog/personalization-option/utils";

/** The list of controlled slots, with their order (Package only) and an "Add Slots" modal trigger. */
export const SlotsSection = observer(function SlotsSection(props: {
  tid: TestIds;
  formState: ObjectState<FormValue>;
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[];
}) {
  const { formState, tid, targetGroups } = props;
  const { openModal } = useModal();
  const { designPackageId, designPackageVersionId } = useRouteParams<{
    designPackageId: string;
    designPackageVersionId: string;
  }>();
  const isPackageType = formState.type.value === ReadyPlanOptionGroupPersonalizationType.Package;
  // Sort placeholders by order for display (ordered slots first, then unordered)
  const sortedPlaceholders = useComputed(
    () => [...formState.placeholders.rows].sortBy((row) => row.order.value ?? Infinity),
    [formState.placeholders],
  );

  return (
    <div>
      {sortedPlaceholders.map((tli) => (
        <div key={tli.id.value} {...tid.slotLocation} css={Css.df.aic.gap2.mb1.$}>
          {isPackageType && (
            <div css={Css.wPx(80).$}>
              <BoundNumberField
                field={tli.order}
                label="Order"
                labelStyle="hidden"
                placeholder="Order"
                disabled={formState.readOnly}
                {...tid.slotOrder}
              />
            </div>
          )}
          <div css={Css.fg1.$}>
            {tli.data.value.name} - {tli.data.value.location.displayLocationPath}
          </div>
          {formState.placeholders.rows.length > 1 && (
            <IconButton
              icon="remove"
              disabled={formState.readOnly}
              onClick={() => {
                formState.placeholders.remove(tli.value);
                // For package groups, remove products that are now invalid based on the removed placeholder
                if (formState.type.value === ReadyPlanOptionGroupPersonalizationType.Package) {
                  formState.packageOptions.rows.forEach((po) => {
                    po.products.rows
                      .filter(
                        (p) =>
                          !formState.placeholders.rows.some(
                            (pl) =>
                              pl.data.value.materialVariant?.id === p.placeholderMaterialId.value &&
                              (!formState.groupByFeature.value || pl.data.value.location.id === p.locationId.value),
                          ),
                      )
                      .forEach((p) => po.products.remove(p.value));
                  });
                }
              }}
              {...tid.slotRemove}
            />
          )}
        </div>
      ))}
      {formState.type.value ? (
        <div css={Css.mt2.$}>
          <Button
            label="Add Slots"
            variant="secondary"
            disabled={formState.readOnly}
            icon="plus"
            onClick={() =>
              openModal({
                content: (
                  <AddSlotModal
                    designPackageId={designPackageId}
                    designPackageVersionId={designPackageVersionId}
                    existingPlaceholderIds={formState.placeholders.value.map((p) => p.id)}
                    onAddSlots={(placeholders) => {
                      const rpoIdToRpogId = buildRpoIdToRpogIdMap(targetGroups);
                      placeholders.forEach((p) => {
                        formState.placeholders.add({ id: p.id, data: fragment(p) });
                        addExistingMaterials(formState, p, rpoIdToRpogId);
                      });
                    }}
                    // If we are adding slots for a product, we want to filter the slots by material variant
                    materialVariantId={maybeFilterByPlaceholder(formState)}
                  />
                ),
              })
            }
          />
        </div>
      ) : null}
    </div>
  );
});

/** The "Apply to" target-group chips. */
export const TargetGroupsSection = observer(function TargetGroupsSection(props: {
  tid: TestIds;
  formState: ObjectState<FormValue>;
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[];
}) {
  const { tid, formState, targetGroups } = props;
  return (
    <div>
      {targetGroups.nonEmpty ? (
        <ToggleChipGroup
          {...tid.targetGroupChipGroup}
          label="Target Groups"
          labelStyle="hidden"
          options={targetGroups.map((tg) => ({ label: tg.name, value: tg.id }))}
          values={formState.personalizationTargetGroupIds?.value ?? []}
          readonly={formState.readOnly}
          onChange={(tgIds) => {
            formState.personalizationTargetGroupIds.set(tgIds);
            // Clear option combinations when target groups change
            [...formState.productOptions.rows, ...formState.packageOptions.rows].forEach((row) =>
              row.personalizationTargetCombinations.set([]),
            );
          }}
        />
      ) : (
        <div css={Css.asc.sm.$}>No options available</div>
      )}
    </div>
  );
});
